import { CascadiaJsonFormContext } from "@certn/forms/src/CascadiaJsonFormContext";
import { ArrayLayoutProps } from "@jsonforms/core";
import get from "lodash.get";
import { useTranslation } from "react-i18next";
import { useItemCount } from "../../item-count-context";
import {
  getItemName,
  getMatchingConditionDefinition,
  getMinItems,
} from "../../utils";
import { useContext, useEffect, useState } from "react";
import { useIsNewItem } from "../../hooks";

function getItemText(values: any, path: string, itemHeadingPath?: string) {
  return get(
    values,
    path + (itemHeadingPath ? "." + itemHeadingPath : undefined)
  );
}

interface ItemHeadingExtraTextOptions {
  values: any;
  path: string;
  itemHeadingPath?: string | string[];
  isNew: boolean;
  separator?: string;
  position: "prefix" | "suffix";
}

function getItemHeadingExtraText(options: ItemHeadingExtraTextOptions) {
  const {
    values,
    path,
    itemHeadingPath,
    isNew,
    separator = " -  ",
    position,
  } = options;
  let itemText = "";

  if (Array.isArray(itemHeadingPath)) {
    itemText = itemHeadingPath
      .map((curr) => getItemText(values, path, curr))
      .filter((text) => text)
      .join(", ");
  } else if (typeof itemHeadingPath === "string") {
    itemText = getItemText(values, path, itemHeadingPath);
  }

  if (!isNew) {
    if (typeof itemText === "string" && itemText.trim().length > 0) {
      return position === "prefix"
        ? `${itemText}${separator}`
        : `${separator}${itemText}`;
    }
  }
  return "";
}

interface PrefixSuffixOptions {
  path: string | string[];
  separator?: string;
}
interface ConditionDefinition {
  condition: Record<string, unknown>;
  value: string;
  position?: "prefix" | "suffix" | "both";
}

interface headingTextObject {
  baseHeadingText: string;
  type?: "count" | "conditionalText";
  prefix?: PrefixSuffixOptions;
  suffix?: PrefixSuffixOptions;
  conditionDefinitions?: ConditionDefinition[];
}

function getPrefixesAndSuffixes(
  item: Record<string, unknown>,
  headingText: headingTextObject,
  path: string,
  values: any,
  isNew: boolean
) {
  const matchedConditions = getMatchingConditionDefinition(
    item,
    headingText?.conditionDefinitions
  );
  const suffixPaths = [
    ...(matchedConditions?.position === "both" ||
    matchedConditions?.position === "suffix"
      ? [matchedConditions?.value]
      : []),
    headingText?.suffix?.path,
  ];
  const suffix = getItemHeadingExtraText({
    values: values,
    path: path,
    itemHeadingPath: suffixPaths.filter(Boolean),
    isNew: isNew,
    position: "suffix",
    separator: headingText?.suffix?.separator,
  });

  const prefixPaths = [
    ...(matchedConditions?.position === "both" ||
    matchedConditions?.position === "prefix"
      ? [matchedConditions?.value]
      : []),
    headingText?.prefix?.path,
  ];
  const prefix = getItemHeadingExtraText({
    values: values,
    path: path,
    itemHeadingPath: prefixPaths.filter(Boolean),
    isNew: isNew,
    position: "prefix",
    separator: headingText?.prefix?.separator,
  });
  return { prefix, suffix };
}

export function useItemHeadingText(
  index: number,
  label: string,
  minItems: number | undefined,
  uischema: ArrayLayoutProps["uischema"],
  path: string
) {
  const { options } = uischema;
  const { t } = useTranslation();
  const { values } = useContext(CascadiaJsonFormContext);
  const [currentHeadingText, setCurrentHeadingText] = useState<string>();
  const { incrementAdditionalCount, incrementMainCount, resetCounts } =
    useItemCount();
  const itemName = getItemName(uischema, label, t("Field"));
  const isAdditional = minItems && index >= getMinItems(minItems, uischema);
  const isNew = useIsNewItem(path);

  const getHeadingOrNewItemText = (baseHeading: string) => {
    if (isNew) {
      return options?.newItemHeading ?? t("New {{itemName}}", { itemName });
    }
    return baseHeading;
  };

  const getBaseHeadingText = () => {
    if (isAdditional && options?.additionalHeadingText !== undefined) {
      // null is no heading, undefined is default heading
      if (options?.additionalHeadingText !== null) {
        const additionalHeadingCounter = incrementAdditionalCount();
        const prefix = options.additionalHeadingText;
        return getHeadingOrNewItemText(
          additionalHeadingCounter === 1
            ? prefix
            : `${prefix} ${additionalHeadingCounter}`
        );
      }
    } else {
      // If options?.additionalHeadingText is undefined, additional items share the same heading text and counter as the required
      if (options?.headingText === null) {
        return undefined;
      }

      if (
        typeof options?.headingText === "object" &&
        options?.headingText !== null
      ) {
        const headingTextObj = options?.headingText as headingTextObject;

        const item = get(values, path);
        const { prefix, suffix } = getPrefixesAndSuffixes(
          item,
          headingTextObj,
          path,
          values,
          isNew
        );

        let baseHeading = headingTextObj.baseHeadingText || itemName;
        if (headingTextObj.type === "count") {
          baseHeading = `${baseHeading} ${incrementMainCount()}`;
        }

        return getHeadingOrNewItemText(prefix + baseHeading + suffix);
      }

      if (options?.doNotRenderCounter) {
        return getHeadingOrNewItemText(options?.headingText ?? itemName);
      }
      const mainHeadingCounter = incrementMainCount();
      const baseHeading = options?.headingText ?? itemName;
      return getHeadingOrNewItemText(`${baseHeading} ${mainHeadingCounter}`);
    }
  };

  useEffect(() => {
    const baseHeadingText = getBaseHeadingText();
    if (baseHeadingText) {
      setCurrentHeadingText(baseHeadingText);
    } else {
      setCurrentHeadingText(undefined);
    }
    return () => resetCounts();
  }, [path, index, incrementAdditionalCount, incrementMainCount]);

  return currentHeadingText;
}
